import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { BRAND } from '../brand';

const TrackOrder = () => {
  const { folio } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        // El folio viene como GB-XXXXXXXX, necesitamos extraer el ID
        const orderId = folio.replace('GB-', '').toLowerCase();
        const response = await api.get(`/track/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('No se encontró la orden');
      } finally {
        setLoading(false);
      }
    };

    if (folio) {
      fetchOrder();
    }
  }, [folio]);

  const getStatusInfo = (status) => {
    const statusMap = {
      'pending': { label: 'Pendiente', color: 'bg-yellow-500', icon: '⏳', description: 'Tu orden ha sido creada y está pendiente de pago.' },
      'payment_uploaded': { label: 'Comprobante Recibido', color: 'bg-blue-500', icon: '📄', description: 'Hemos recibido tu comprobante de pago y está siendo verificado.' },
      'validating': { label: 'Validando Pago', color: 'bg-blue-600', icon: '🔍', description: 'Estamos verificando tu pago.' },
      'processing': { label: 'En Proceso', color: 'bg-purple-500', icon: '⚙️', description: 'Tu transferencia está siendo procesada.' },
      'completed': { label: 'Completado', color: 'bg-green-500', icon: '✅', description: '¡Tu transferencia ha sido completada exitosamente!' },
      'rejected': { label: 'Rechazado', color: 'bg-red-500', icon: '❌', description: 'La orden fue rechazada. Contacta soporte para más información.' },
      'cancelled': { label: 'Cancelado', color: 'bg-gray-500', icon: '🚫', description: 'Esta orden ha sido cancelada.' }
    };
    return statusMap[status] || { label: status, color: 'bg-gray-400', icon: '❓', description: '' };
  };

  const getProgressSteps = (currentStatus) => {
    const steps = [
      { key: 'pending', label: 'Orden Creada' },
      { key: 'payment_uploaded', label: 'Pago Recibido' },
      { key: 'processing', label: 'Procesando' },
      { key: 'completed', label: 'Completado' }
    ];

    const statusOrder = ['pending', 'payment_uploaded', 'validating', 'processing', 'completed'];
    const currentIndex = statusOrder.indexOf(currentStatus);

    return steps.map((step, index) => {
      let stepStatus = 'pending';
      if (currentStatus === 'rejected' || currentStatus === 'cancelled') {
        stepStatus = index === 0 ? 'completed' : 'error';
      } else if (index < currentIndex || (index === currentIndex && currentStatus === 'completed')) {
        stepStatus = 'completed';
      } else if (index === currentIndex || (currentStatus === 'validating' && step.key === 'payment_uploaded')) {
        stepStatus = 'current';
      }
      return { ...step, status: stepStatus };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Buscando tu orden...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Orden no encontrada</h1>
          <p className="text-gray-600 mb-6">No pudimos encontrar una orden con el folio: {folio}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition"
          >
            Ir al inicio
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const progressSteps = getProgressSteps(order.status);

  // Formatear fecha
  let formattedDate = 'N/A';
  if (order.created_at) {
    try {
      const date = new Date(order.created_at);
      formattedDate = date.toLocaleDateString('es-CL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit', timeZone: 'America/Santiago' });
    } catch (e) {
      formattedDate = order.created_at.slice(0, 16).replace('T', ' ');
    }
  }

  const symbols = { 'CLP': '$', 'BOB': 'Bs', 'ARS': '$', 'PEN': 'S/', 'COP': '$', 'VES': 'Bs' };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-green-700 mb-1">{BRAND.name}</h1>
          <p className="text-gray-500 text-sm">Seguimiento de Envío</p>
        </div>

        {/* Card Principal */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-green-500">
          {/* Header con estado */}
          <div className={`${statusInfo.color} text-white p-6 text-center`}>
            <div className="text-4xl mb-2">{statusInfo.icon}</div>
            <h2 className="text-xl font-bold">{statusInfo.label}</h2>
            <p className="text-sm opacity-90 mt-1">{statusInfo.description}</p>
          </div>

          {/* Folio */}
          <div className="bg-gray-50 px-6 py-3 border-b flex justify-between items-center">
            <span className="text-gray-500 text-sm">Folio</span>
            <span className="font-mono font-bold text-gray-800">GB-{order.id?.slice(0, 8).toUpperCase()}</span>
          </div>

          {/* Progress Steps */}
          <div className="px-6 py-4 border-b">
            <div className="flex justify-between">
              {progressSteps.map((step, index) => (
                <div key={step.key} className="flex flex-col items-center flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold mb-1 ${
                    step.status === 'completed' ? 'bg-green-500' :
                    step.status === 'current' ? 'bg-blue-500 animate-pulse' :
                    step.status === 'error' ? 'bg-red-500' :
                    'bg-gray-300'
                  }`}>
                    {step.status === 'completed' ? '✓' : 
                     step.status === 'error' ? '✗' : 
                     index + 1}
                  </div>
                  <span className="text-xs text-gray-500 text-center">{step.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Detalles */}
          <div className="p-6 space-y-4">
            {/* Fecha */}
            <div className="flex justify-between">
              <span className="text-gray-500">Fecha</span>
              <span className="font-medium">{formattedDate}</span>
            </div>

            {/* Monto enviado */}
            <div className="flex justify-between">
              <span className="text-gray-500">Envía</span>
              <span className="font-medium">
                {symbols[order.currency_from] || '$'} {order.amount_from?.toLocaleString('es-CL', { timeZone: 'America/Santiago' })} {order.currency_from}
              </span>
            </div>

            {/* Destinatario */}
            <div className="flex justify-between">
              <span className="text-gray-500">Destinatario</span>
              <span className="font-medium">{order.recipient_name || 'N/A'}</span>
            </div>

            {/* País destino */}
            <div className="flex justify-between">
              <span className="text-gray-500">País destino</span>
              <span className="font-medium">{order.destination_country || order.currency_to}</span>
            </div>

            {/* Monto a recibir */}
            <div className="bg-green-50 rounded-xl p-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-green-700 font-semibold">Recibe</span>
                <span className="text-2xl font-bold text-green-700">
                  {symbols[order.currency_to] || 'Bs'} {order.amount_to?.toLocaleString('es-CL', { minimumFractionDigits: 2, timeZone: 'America/Santiago' })}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-sm text-gray-500 mb-2">¿Necesitas ayuda?</p>
            <a
              href={`https://wa.me/${BRAND?.landing?.footerWhatsAppNumber || ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition"
            >
              <span>💬</span> WhatsApp
            </a>
          </div>
        </div>

        {/* Branding */}
        <div className="text-center mt-6 text-gray-400 text-sm">
          <p>{BRAND.name} · Transferencias Internacionales</p>
          <p>{BRAND.website}</p>
        </div>
      </div>
    </div>
  );
};

export default TrackOrder;
